import { forEach, template } from "lodash-es";
import type { MessageType } from "stores/modules/entities.room.messages/records";

export type RoomLogHtmlMessage = {
  name: string;
  iconDataUrl: string | null;
  color: string;
  text: string;
  type: MessageType;
  roll: {
    result: string;
    success: boolean;
    failure: boolean;
    critical: boolean;
    fumble: boolean;
  } | null;
  edited: boolean;
  channel: string;
  channelName: string;
  createdAt: number;
};

type RoomLogHtmlLabels = {
  edited: string;
};

type RenderRoomLogHtmlOptions = {
  roomName: string;
  channelName: string;
  showChannelName: boolean;
  messages: RoomLogHtmlMessage[];
  language: string;
  labels: RoomLogHtmlLabels;
};

type RoomLogHtmlAvatarStyle = {
  className: string;
  dataUrl: string;
};

type RoomLogHtmlTemplateMessage = RoomLogHtmlMessage & {
  avatarClassName: string | null;
  speakerColor: string;
  rollClassName: string;
  createdAtISOString: string;
  createdAtLabel: string;
};

const RoomLogHtmlTemplate = `<!DOCTYPE html>
<html lang="<%- htmlLanguage %>">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title><%- roomName %> [<%- channelName %>]</title>
    <style>
      :root {
        color-scheme: dark;
        color: #eee;
        background: #121212;
      }

      * {
        box-sizing: border-box;
      }

      body {
        min-width: 280px;
        margin: 0;
        background: #121212;
      }

      .message-list {
        width: 100%;
        max-width: 960px;
        margin: 0 auto;
        padding: 0 24px 48px;
      }

      .log-title {
        margin: 24px 0;
        font-size: 24px;
      }

      .message {
        display: grid;
        grid-template-columns: 40px minmax(0, 1fr);
        column-gap: 12px;
        padding: 16px 0;
        border-bottom: 1px solid #262626;
      }

      .message.system {
        display: block;
        padding-left: 52px;
        color: #888;
      }

      .avatar,
      .avatar-spacer {
        width: 40px;
        height: 40px;
      }

      .avatar {
        display: block;
        background-repeat: no-repeat;
        background-size: 100% 100%;
      }

<% forEach(avatarStyles, function(avatarStyle) { %>
      .<%- avatarStyle.className %> { background-image: url("<%- avatarStyle.dataUrl %>"); }
<% }); %>

      .message-content {
        min-width: 0;
      }

      .message-header {
        display: flex;
        align-items: baseline;
        flex-wrap: wrap;
        gap: 8px;
      }

      .speaker {
        color: var(--speaker-color);
        font-size: 14px;
        font-weight: bold;
      }

      .timestamp,
      .edited,
      .channel-name {
        color: #888;
        font-size: 12px;
      }

      .message-text,
      .roll-result {
        font-size: 16px;
        line-height: 1.6;
        overflow-wrap: anywhere;
        white-space: pre-wrap;
      }

      .roll-result {
        display: block;
        color: #888;
      }

      .roll-result.success,
      .roll-result.critical {
        color: #2196f3;
      }

      .roll-result.failure,
      .roll-result.fumble {
        color: #dc004e;
      }
    </style>
  </head>
  <body>
    <main class="message-list">
      <h1 class="log-title"><%- roomName %> [<%- channelName %>]</h1>
<% forEach(messages, function(message) { %>
<% if (message.type === "system") { %>
      <article class="message system" data-channel="<%- message.channel %>">
<% if (showChannelName && message.channelName) { %>
        <div class="message-header">
          <span class="channel-name">[<%- message.channelName %>]</span>
        </div>
<% } %>
        <div class="message-text"><%- message.text %></div>
      </article>
<% } else { %>
      <article class="message" data-channel="<%- message.channel %>">
<% if (message.avatarClassName) { %>
        <span class="avatar <%- message.avatarClassName %>" aria-hidden="true"></span>
<% } else { %>
        <span class="avatar-spacer" aria-hidden="true"></span>
<% } %>
        <div class="message-content">
          <div class="message-header">
            <span class="speaker" style="--speaker-color:<%- message.speakerColor %>"><%- message.name || "noname" %></span>
            <time class="timestamp" datetime="<%- message.createdAtISOString %>"><%- message.createdAtLabel %></time>
<% if (showChannelName && message.channelName) { %>
            <span class="channel-name">[<%- message.channelName %>]</span>
<% } %>
<% if (message.edited) { %>
            <span class="edited">[<%- labels.edited %>]</span>
<% } %>
          </div>
          <div class="message-text"><%- message.text %></div>
<% if (message.roll && message.roll.result) { %>
          <span class="roll-result<% if (message.rollClassName) { %> <%- message.rollClassName %><% } %>"><%- message.roll.result %></span>
<% } %>
        </div>
      </article>
<% } %>
<% }); %>
    </main>
  </body>
</html>`;

const renderRoomLogHtmlTemplate = template(RoomLogHtmlTemplate);

const getSpeakerColor = (color: string) =>
  /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(color)
    ? color
    : "#eee";

const getRollClassName = (message: RoomLogHtmlMessage) => {
  if (message.roll?.critical) return "critical";
  if (message.roll?.fumble) return "fumble";
  if (message.roll?.success) return "success";
  if (message.roll?.failure) return "failure";
  // 繰り返しロールでは成否フラグが付かないことがあるため、ルームチャットと同様に結果の文言から判定する
  if (message.roll?.result.includes("成功")) return "success";
  if (message.roll?.result.includes("スペシャル")) return "success";
  if (message.roll?.result.includes("失敗")) return "failure";
  return "";
};

const isImageDataUrl = (value: string) =>
  /^data:image\/[a-z0-9.+-]+;base64,[a-z0-9+/=]+$/i.test(value);

// 同じ画像データをメッセージごとに埋め込まないよう、CSSから参照する
const getAvatarStyles = (messages: RoomLogHtmlMessage[]) => {
  const classNames = new Map<string, string>();
  const styles: RoomLogHtmlAvatarStyle[] = [];

  messages.forEach(({ iconDataUrl }) => {
    if (
      iconDataUrl &&
      !classNames.has(iconDataUrl) &&
      isImageDataUrl(iconDataUrl)
    ) {
      const className = `avatar-image-${classNames.size}`;
      classNames.set(iconDataUrl, className);
      styles.push({ className, dataUrl: iconDataUrl });
    }
  });

  return {
    classNames,
    styles,
  };
};

export const renderRoomLogHTML = ({
  roomName,
  channelName,
  showChannelName,
  messages,
  language,
  labels,
}: RenderRoomLogHtmlOptions) => {
  const htmlLanguage = language === "zhTw" ? "zh-TW" : language || "ja";
  const dateTimeFormat = new Intl.DateTimeFormat(htmlLanguage, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const avatarStyles = getAvatarStyles(messages);
  const templateMessages: RoomLogHtmlTemplateMessage[] = messages.map(
    (message) => {
      const createdAt = new Date(message.createdAt);
      return {
        ...message,
        avatarClassName: message.iconDataUrl
          ? avatarStyles.classNames.get(message.iconDataUrl) || null
          : null,
        speakerColor: getSpeakerColor(message.color),
        rollClassName: getRollClassName(message),
        createdAtISOString: createdAt.toISOString(),
        createdAtLabel: dateTimeFormat.format(createdAt),
      };
    }
  );

  return renderRoomLogHtmlTemplate({
    roomName,
    channelName,
    showChannelName,
    htmlLanguage,
    avatarStyles: avatarStyles.styles,
    messages: templateMessages,
    labels,
    forEach,
  });
};
